/**
 * Product Configurator - Event-Driven Service-Based Architecture
 * VERSION: 2024-02-26 - Refactored for clarity and maintainability
 * 
 * ARCHITECTURE:
 * - Event-driven communication via EventBus
 * - Single-purpose, disposable functions
 * - Clear handler entry points for each event type
 * - Consumables initialized once (never updated - they're always Purchase)
 * - Rental items (main product, modules, optional) updated on date/transaction changes
 * 
 * KEY HANDLERS:
 * - handleDatesChanged: Entry point for rental date changes
 * - handleTransactionTypeChanged: Entry point for Rent/Purchase toggle
 * - handleVariantSelectionChanged: Entry point for manual variant selection
 * 
 * SERVICES (Business Logic):
 * - VariantSelector: Variant selection for all items
 * - PriceCalculator: Price fetching for all items
 * - CartManager: All cart operations
 * - ProductDataCache: Product data fetching
 */

console.log('🚀 js-product-configurator.js LOADED', {
  version: '2024-02-26 - Refactored Architecture',
  architecture: 'Event-driven with single-purpose functions',
  services: ['VariantSelector', 'PriceCalculator', 'CartManager', 'ProductDataCache']
});

class ProductConfigurator {
  constructor() {
    // Variant data
    this.variants = [];
    this.currentVariant = null;
    this.productHandle = null;
    
    // State
    this.isRental = true;
    this.currentTransactionType = 'Rent';
    this.modulesRequired = true;
    
    // Rental dates
    this.rentalStartDate = null;
    this.rentalEndDate = null;
    this.calculatedRentalDays = null;
    
    // UI state
    this.summaryQuantityBound = false;
    
    // Track previous selections for URL updates
    this.previousRequiredModulesIds = '';
    this.previousConsumablesState = '';
    this.previousOptionalModulesState = '';
    
    // Initialize
    this.init();
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize the configurator
   */
  init() {
    if (!this.cacheElements()) {
      console.log('ProductConfigurator: Required elements not found');
      return;
    }
    
    this.loadVariantsData();
    this.bindDOMEvents();
    this.subscribeToEventBus();
    this.initializeState();
  }

  /**
   * Cache DOM elements
   */
  cacheElements() {
    // Get section ID
    const configurator = document.querySelector('.rr-product-configurator');
    const form = document.querySelector('form[action*="/cart/add"]');
    
    let sectionId = '';
    if (configurator) {
      const dataConfigId = configurator.getAttribute('data-configurator-id');
      if (dataConfigId) {
        sectionId = dataConfigId.replace('product-configurator-', '');
      }
    }
    
    if (!sectionId && form?.id) {
      sectionId = form.id.replace('product-form-', '');
    }
    
    this.sectionId = sectionId;
    
    // Forms
    this.productForm = document.getElementById(`product-form-${this.sectionId}`) || form;
    if (!this.productForm) {
      console.error('❌ Product form not found');
      return false;
    }
    
    // Variant selects
    this.variantSelectsContainer = document.getElementById(`variant-selects-${this.sectionId}`) || 
                                   document.querySelector('variant-selects');
    this.variantSelects = this.productForm.querySelectorAll('select[name^="options"]');
    
    // Price elements
    this.priceElement = document.getElementById(`product-price-amount-${this.sectionId}`) || 
                       document.querySelector('.product-price__amount');
    this.pricePeriodElement = document.getElementById(`product-price-period-${this.sectionId}`) || 
                             document.querySelector('.product-price__period');
    
    // Variant and cart elements
    this.hiddenVariantInput = document.getElementById(`variant-id-${this.sectionId}`) || 
                             document.querySelector('input[name="id"]');
    this.addToCartButton = document.getElementById(`add-to-cart-${this.sectionId}`) || 
                          document.querySelector('button[name="add"]');
    this.availabilityStatus = document.querySelector('.availability-status');
    this.validationMessage = document.getElementById(`validation-message-${this.sectionId}`);
    
    // Transaction type (use section-specific name attribute)
    this.transactionTypeRadios = document.querySelectorAll(`input[name="transaction-type-${this.sectionId}"]`);
    
    // Required modules
    this.requiredModulesContainer = document.getElementById(`required-modules-${this.sectionId}`) || 
                                   document.querySelector('.rr-required-modules');
    const configuratorElement = document.querySelector('.rr-product-configurator') || this.productForm;
    this.requiredModuleCheckboxes = configuratorElement?.querySelectorAll('.required-module-checkbox, .module-checkbox') || [];
    this.requiredModuleCount = document.querySelector('[data-required-count]');
    this.requiredModuleError = document.getElementById(`modules-error-${this.sectionId}`) || 
                              document.querySelector('[data-required-error]');
    this.selectedCountSpan = document.querySelector('[data-selected-count]');

    // Zero-selected approval (inline acknowledgement, rental-only)
    this.zeroApprovalContainer = document.getElementById(`zero-module-approval-${this.sectionId}`);
    this.zeroApprovalCheckbox = document.getElementById(`zero-module-approval-checkbox-${this.sectionId}`);
    this.zeroApprovalEnabled = !!this.zeroApprovalContainer;
    
    // Optional addons
    this.optionalAddonsContainer = document.getElementById(`optional-addons-${this.sectionId}`) || 
                                  document.querySelector('.rr-optional-addons');
    this.addonGroups = document.querySelectorAll('.rr-addon-group');
    
    // Quantity controls
    this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`) || 
                        document.querySelector('.quantity-input');
    this.quantityButtons = document.querySelectorAll('.quantity-btn');
    
    // Summary elements
    this.summaryContainer = document.getElementById(`configuration-summary-${this.sectionId}`);
    this.summaryItems = document.getElementById(`configuration-summary-items-${this.sectionId}`);
    this.subtotalAmount = document.getElementById(`configuration-subtotal-${this.sectionId}`);
    
    // Warning elements
    this.usedEquipmentWarning = document.getElementById(`used-equipment-warning-${this.sectionId}`);
    
    return true;
  }

  /**
   * Load variants data from JSON script
   */
  loadVariantsData() {
    if (!this.variantSelectsContainer) {
      console.warn('⚠️ No variant-selects container found - product may have only default variant');
      if (this.hiddenVariantInput?.value) {
        console.log('ℹ️ Using hidden variant input value:', this.hiddenVariantInput.value);
        this.variants = [];
      }
      return;
    }
    
    const variantDataScript = this.variantSelectsContainer.querySelector('script[type="application/json"]');
    if (!variantDataScript) {
      console.warn('⚠️ No variant data script found in variant-selects container');
      return;
    }
    
    try {
      this.variants = JSON.parse(variantDataScript.textContent);
      console.log('✅ Loaded variants:', this.variants.length, 'variants');
      
      this.currentVariant = this.variants.find(
        v => v.id === parseInt(this.hiddenVariantInput?.value)
      ) || this.variants[0];
      
      console.log('✅ Current variant set to:', this.currentVariant?.title || 'none');
      
      // Get product handle from multiple sources
      if (this.currentVariant?.product?.handle) {
        this.productHandle = this.currentVariant.product.handle;
      }
      
      if (!this.productHandle && this.productForm) {
        const formAction = this.productForm.action;
        const urlParts = formAction.split('/products/');
        if (urlParts.length > 1) {
          this.productHandle = urlParts[1].split('/')[0].split('?')[0];
        }
      }
      
      if (!this.productHandle) {
        const configurator = document.querySelector('.rr-product-configurator');
        this.productHandle = configurator?.getAttribute('data-product-handle');
      }
      
      if (!this.productHandle && window.location.pathname.includes('/products/')) {
        const pathParts = window.location.pathname.split('/products/');
        if (pathParts.length > 1) {
          this.productHandle = pathParts[1].split('/')[0].split('?')[0];
        }
      }
      
      if (this.productHandle) {
        console.log('✅ Product handle loaded:', this.productHandle);
      } else {
        console.error('❌ Failed to determine product handle - variant selection will not work');
      }
    } catch (error) {
      console.error('Error parsing variant data:', error);
    }
  }

  /**
   * Initialize state
   */
  initializeState() {
    // Initialize transaction types and select appropriate variants
    this.initializeTransactionTypes();
    
    // Update isRental flag based on the transaction type that was just set
    const selectedType = document.querySelector(`input[name="transaction-type-${this.sectionId}"]:checked`);
    this.isRental = selectedType?.value !== 'Purchase';
    this.currentTransactionType = selectedType?.value || 'Rent';
    
    console.log('🎯 initializeState', {
      currentTransactionType: this.currentTransactionType,
      isRental: this.isRental,
      currentVariant: this.currentVariant?.title || 'none'
    });
    
    // Load selections from URL if present
    this.loadMainQuantityFromURL();
    this.loadRequiredModulesFromURL();
    this.loadConsumablesFromURL();
    this.loadOptionalModulesFromURL();
    
    // Initialize previous state tracking AFTER loading from URL
    const selectedModules = this.getSelectedRequiredModules();
    this.previousRequiredModulesIds = selectedModules
      .map(cb => cb.dataset.id)
      .filter(id => id)
      .join(',');
    
    this.previousConsumablesState = this.getConsumablesState();
    this.previousOptionalModulesState = this.getOptionalModulesState();
    
    console.log('🎯 Initial state from URL', {
      requiredModules: this.previousRequiredModulesIds || '(none)',
      consumables: this.previousConsumablesState || '(none)',
      optionalModules: this.previousOptionalModulesState || '(none)'
    });
    
    // Ensure price display is set
    if (this.currentVariant) {
      console.log('💰 Ensuring price boxes are updated for variant:', this.currentVariant.title);
      this.updateMainProductDisplay();
    } else {
      console.error('❌ No current variant set after initialization!');
    }
    
    // Initialize consumable prices ONCE (they never change - always Purchase)
    this.initializeConsumablePrices();
    
    // Update visibility and prices
    this.updateConfiguratorVisibility();
    this.updateSummaryDisplay();
    
    // Store button text attributes
    if (this.addToCartButton) {
      const addText = this.addToCartButton.querySelector('span')?.textContent || 'Add to Cart';
      this.addToCartButton.setAttribute('data-add-text', addText);
      this.addToCartButton.setAttribute('data-sold-out-text', 'Sold Out');
    }
    
    // Load dates from cart
    this.loadDatesFromCart();
  }

  // ============================================================================
  // EVENT BUS SUBSCRIPTIONS
  // ============================================================================

  /**
   * Subscribe to EventBus events
   */
  subscribeToEventBus() {
    if (!window.eventBus) {
      console.warn('⚠️ EventBus not available');
      return;
    }
    
    console.log('📡 ProductConfigurator subscribing to events');
    
    // Listen for date changes
    window.eventBus.on('DATES_CHANGED', (data) => this.handleDatesChanged(data));
    
    // Listen for transaction type changes
    window.eventBus.on('TRANSACTION_TYPE_CHANGED', (data) => this.handleTransactionTypeChanged(data));
  }

  // ============================================================================
  // EVENT HANDLERS (Entry Points)
  // ============================================================================

  /**
   * HANDLER: Dates changed
   * Entry point for rental date changes
   * Orchestrates: variant selection, price updates, summary updates
   */
  async handleDatesChanged({ startDate, endDate, days }) {
    console.log('📅 handleDatesChanged', { startDate, endDate, days });
    
    // Store dates
    this.rentalStartDate = startDate;
    this.rentalEndDate = endDate;
    this.calculatedRentalDays = days;
    
    // Only process if in rental mode
    if (!this.isRental) {
      console.log('⚠️ Not in rental mode, skipping');
      return;
    }
    
    // Apply loading state
    this.setLoadingState(true);
    
    try {
      // Select main product variant for new dates
      await this.selectMainVariantForDates(startDate, endDate, days);
      
      // Update rental item prices (main product, modules, optional equipment)
      // Skip consumables - they never change
      await this.updateRentalItemPrices(days);
      
      // Update summary display
      this.updateSummaryDisplay();
    } catch (error) {
      console.error('❌ Error in handleDatesChanged:', error);
    } finally {
      // Always remove loading states
      this.setLoadingState(false);
    }
  }

  /**
   * HANDLER: Transaction type changed (Rent vs Purchase)
   * Entry point for transaction type changes
   * Orchestrates: variant selection, visibility updates, price updates, summary updates
   */
  async handleTransactionTypeChanged({ transactionType, isRental, sectionId }) {
    // Only handle events for this section
    if (sectionId !== this.sectionId) {
      return;
    }
    
    console.log('🔄 handleTransactionTypeChanged', {
      transactionType,
      isRental,
      variantBefore: this.currentVariant?.title
    });
    
    // Update state
    this.currentTransactionType = transactionType || 'Rent';
    this.isRental = isRental !== undefined ? isRental : (transactionType !== 'Purchase');
    
    // When switching to purchase, clear rental dates
    if (!this.isRental) {
      this.rentalStartDate = null;
      this.rentalEndDate = null;
      this.calculatedRentalDays = null;
    }
    
    try {
      // Update variant options for new transaction type
      this.updateOptionsForTransactionType(this.currentTransactionType);
      
      // Update visibility (modules, addons, warnings)
      this.updateConfiguratorVisibility();
      
      // Set loading state before updating prices
      this.setLoadingState(true);
      
      // Update prices based on mode
      if (!this.isRental) {
        // Purchase mode: variant already selected by updateOptionsForTransactionType
        // No need to update prices - they're from the variant
      } else {
        // Rental mode: Check if dates exist and reload them
        await this.reloadRentalDatesIfNeeded();
        
        // If we have dates, select the correct variant and update prices
        if (this.rentalStartDate && this.rentalEndDate && this.calculatedRentalDays) {
          console.log('📅 Reloading variant for existing dates', {
            startDate: this.rentalStartDate,
            endDate: this.rentalEndDate,
            days: this.calculatedRentalDays
          });
          
          // Select variant for the date range
          await this.selectMainVariantForDates(
            this.rentalStartDate,
            this.rentalEndDate,
            this.calculatedRentalDays
          );
          
          // Update rental item prices for the duration
          await this.updateRentalItemPrices(this.calculatedRentalDays);
        } else {
          // No dates selected - show 1-day pricing
          console.log('ℹ️ No dates selected, showing 1-day pricing');
          await this.updateRentalItemPrices(1);
        }
      }
      
      // Remove loading state
      this.setLoadingState(false);
      
      // Update summary
      this.updateSummaryDisplay();
      
      // Validate
      this.validateConfiguration();
      
      console.log('✅ handleTransactionTypeChanged COMPLETE', {
        finalVariant: this.currentVariant?.title,
        finalPrice: this.currentVariant?.price,
        rentalDays: this.calculatedRentalDays
      });
    } catch (error) {
      console.error('❌ Error in handleTransactionTypeChanged:', error);
      // Make sure to remove loading state on error
      this.setLoadingState(false);
    }
  }

  /**
   * HANDLER: Variant selection changed
   * Entry point for manual variant dropdown changes
   */
  handleVariantSelectionChanged() {
    const selectedOptions = this.getSelectedOptions();
    const matchingVariant = this.findMatchingVariant(selectedOptions);

    if (matchingVariant) {
      this.updateMainProductDisplay();
      this.validateConfiguration();
      this.updateSummaryDisplay();
    }
  }

  // ============================================================================
  // SINGLE-PURPOSE FUNCTIONS - VARIANT SELECTION
  // ============================================================================

  /**
   * Select main product variant for rental dates
   * ONLY: Selects variant, updates UI with variant info
   */
  async selectMainVariantForDates(startDate, endDate, days) {
    console.log('🔍 selectMainVariantForDates', { startDate, endDate, days });
    
    if (!this.productHandle || !window.VariantSelector) {
      console.warn('⚠️ Cannot select variant - missing handle or service');
      return;
    }
    
    const variant = await VariantSelector.selectVariantForDates(
      this.productHandle,
      startDate,
      endDate
    );
    
    if (variant) {
      console.log('✅ Selected variant for dates:', variant.title);
      
      // Update variant select dropdown
      const secondSelect = this.variantSelects?.[1];
      if (secondSelect) {
        secondSelect.value = variant.option2;
      }
      
      // Update product display (without triggering price updates)
      this.updateProductInfo(variant, true);
    } else {
      console.error('❌ No variant found for dates');
    }
  }

  /**
   * Select main product variant for purchase
   * ONLY: Selects variant based on condition
   */
  selectMainVariantForPurchase(condition = 'New') {
    console.log('🔍 selectMainVariantForPurchase', { condition });
    
    const variant = this.variants.find(v =>
      v.option1?.toLowerCase() === 'purchase' &&
      (condition === null || v.option2 === condition)
    );
    
    if (variant) {
      console.log('✅ Selected purchase variant:', variant.title);
      this.updateProductInfo(variant);
    } else {
      console.warn('⚠️ No purchase variant found, using fallback');
      const fallback = this.variants.find(v => v.option1?.toLowerCase() === 'purchase');
      if (fallback) {
        this.updateProductInfo(fallback);
      }
    }
  }

  // ============================================================================
  // SINGLE-PURPOSE FUNCTIONS - PRICE UPDATES
  // ============================================================================

  /**
   * Update prices for rental items ONLY
   * ONLY: Updates main product, required modules, optional equipment
   * SKIPS: Consumables (they're always Purchase and never change)
   */
  async updateRentalItemPrices(days) {
    console.log('💰 updateRentalItemPrices', { days, isRental: this.isRental });
    
    // Skip if in purchase mode
    if (!this.isRental) {
      console.log('ℹ️ In purchase mode, skipping rental price updates');
      return;
    }

    // Determine duration string
    const duration = this.currentVariant?.option2 || `${days} Day`;

    console.log('🔄 Updating rental item prices for duration:', duration);

    // Update required module prices (skip consumables and items with variant selectors)
    for (const checkbox of this.requiredModuleCheckboxes || []) {
      const itemId = checkbox.dataset.id;
      
      // Skip consumables - they never change
      if (checkbox.dataset.type === 'consumable') {
        console.log(`⏭️ Skipping consumable module: ${itemId}`);
        continue;
      }
      
      // Skip items with variant selector - they manage their own prices
      const variantSelect = document.querySelector(`[data-variant-select="${itemId}"]`);
      if (variantSelect) {
        console.log(`⏭️ Skipping module with variant selector: ${itemId}`);
        continue;
      }
      
      const parentCard = checkbox.closest('.module-option, .module-card');
      if (parentCard) parentCard.classList.add('rr-loading');
      
      await this.updateItemPrice(checkbox, duration);
      
      if (parentCard) parentCard.classList.remove('rr-loading');
    }

    // Update optional addon prices (skip consumables and items with variant selectors)
    const addonItems = document.querySelectorAll('.rr-addon-item[data-type]');
    for (const item of addonItems) {
      const itemId = item.dataset.id;
      
      // Skip consumables - they never change
      if (item.dataset.type === 'consumable') {
        console.log(`⏭️ Skipping consumable addon: ${itemId}`);
        continue;
      }
      
      // Skip items with variant selector - they manage their own prices
      const variantSelect = document.querySelector(`[data-variant-select="${itemId}"]`);
      if (variantSelect) {
        console.log(`⏭️ Skipping addon with variant selector: ${itemId}`);
        continue;
      }
      
      const parentCard = item.closest('.rr-addon-item');
      if (parentCard) parentCard.classList.add('rr-loading');
      
      await this.updateItemPrice(item, duration);
      
      if (parentCard) parentCard.classList.remove('rr-loading');
    }
    
    console.log('✅ Rental item prices updated');
  }

  /**
   * Update individual item price
   * Uses PriceCalculator service or variant selector price
   */
  async updateItemPrice(element, duration) {
    const handle = element.dataset.handle || element.dataset.moduleHandle || element.dataset.addonHandle;
    const itemId = element.dataset.id || element.dataset.moduleId || element.dataset.addonId;
    const itemType = element.dataset.type;
    const priceElement = document.querySelector(`[data-price-for="${itemId}"]`);
    
    if (!priceElement || !handle) return;

    priceElement.textContent = 'Loading...';
    
    try {
      // Check if item has a variant selector (consumables/optional with multiple variants)
      const variantSelect = document.querySelector(`[data-variant-select="${itemId}"]`);
      
      if (variantSelect) {
        // Use price from selected variant
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const price = parseInt(selectedOption.dataset.price) / 100;
        const formattedPrice = window.formatMoney(Math.round(price * 100));
        
        priceElement.innerHTML = `${formattedPrice}<span class="rr-addon-item__price-label"> / per unit</span>`;
        
        // Emit event
        if (window.eventBus) {
          window.eventBus.emit('PRICE_UPDATED', {
            itemId,
            price,
            formattedPrice: formattedPrice
          });
        }
      } else {
        // No variant selector - use PriceCalculator service
        let fetchDuration = duration;
        if (itemType === 'consumable') {
          fetchDuration = 'Purchase';
        } else if (!duration || !this.rentalStartDate || !this.rentalEndDate) {
          fetchDuration = '1 Day';
        }
        
        const price = window.PriceCalculator 
          ? await PriceCalculator.getPriceForDuration(handle, fetchDuration)
          : null;
        
        if (price !== null) {
          const formattedPrice = window.formatMoney(Math.round(price * 100));
          
          if (itemType === 'consumable') {
            priceElement.innerHTML = `${formattedPrice}<span class="rr-addon-item__price-label"> / per unit</span>`;
          } else {
            // Show days only for rental items with valid dates
            let daysText = '';
            if (this.isRental && this.rentalStartDate && this.rentalEndDate && this.calculatedRentalDays > 0) {
              daysText = ` <span class="rr-addon-item__price-label">for ${this.calculatedRentalDays} day${this.calculatedRentalDays !== 1 ? 's' : ''}</span>`;
            }
            priceElement.innerHTML = `${formattedPrice}${daysText}`;
          }
          
          // Emit event
          if (window.eventBus) {
            window.eventBus.emit('PRICE_UPDATED', {
              itemId,
              price,
              formattedPrice: formattedPrice
            });
          }
        } else {
          priceElement.textContent = 'Price unavailable';
        }
      }
    } catch (error) {
      console.error('❌ Error updating price:', error);
      priceElement.textContent = 'Error';
    }
  }

  /**
   * Initialize consumable prices ONCE
   * Consumables are always Purchase items and never change
   */
  async initializeConsumablePrices() {
    console.log('💰 Initializing consumable prices (one-time)');
    
    const consumableItems = document.querySelectorAll('.rr-addon-item[data-type="consumable"]');
    
    for (const item of consumableItems) {
      const itemId = item.dataset.id;
      
      // Skip items with variant selector - they initialize their own prices
      const variantSelect = document.querySelector(`[data-variant-select="${itemId}"]`);
      if (variantSelect) {
        console.log(`ℹ️ Skipping consumable ${itemId} - has variant selector`);
        continue;
      }
      
      const parentCard = item.closest('.rr-addon-item');
      if (parentCard) parentCard.classList.add('rr-loading');
      
      console.log(`💰 Fetching price for consumable: ${itemId}`);
      await this.updateItemPrice(item, 'Purchase');
      
      if (parentCard) parentCard.classList.remove('rr-loading');
    }
    
    console.log('✅ Consumable prices initialized');
  }

  // ============================================================================
  // SINGLE-PURPOSE FUNCTIONS - UI UPDATES
  // ============================================================================

  /**
   * Update main product display (price, availability, variant info)
   * ONLY: Updates UI elements for main product
   */
  updateMainProductDisplay() {
    const variant = this.currentVariant;
    
    if (!variant) {
      console.error('❌ updateMainProductDisplay called with no variant!');
      return;
    }

    console.log('💰 updateMainProductDisplay', {
      variant: variant.title,
      price: variant.price
    });

    // Update summary subheading (variant duration/condition)
    const summarySubheadingElement = document.getElementById(`main-product-subheading-${this.sectionId}`);
    if (summarySubheadingElement && variant.option2) {
      summarySubheadingElement.textContent = variant.option2;
    }

    // Update price display
    if (this.priceElement) {
      this.priceElement.textContent = window.formatMoney(variant.price);
    }

    // Update price period
    if (this.pricePeriodElement) {
      const opt1Lower = variant.option1?.toLowerCase() || '';
      const isPurchase = opt1Lower === 'purchase';

      if (isPurchase) {
        this.pricePeriodElement.textContent = '';
      } else if (variant.option2) {
        this.pricePeriodElement.textContent = ` / per ${variant.option2.toLowerCase()}`;
      }
    }

    // Update hidden variant ID
    if (this.hiddenVariantInput) {
      this.hiddenVariantInput.value = variant.id;
    }

    // Update availability
    this.updateAvailability(variant);
    
    // Update used equipment warning
    this.updateUsedEquipmentWarning(variant);

    // Emit events
    if (window.eventBus) {
      window.eventBus.emit('VARIANT_SELECTED', {
        itemType: 'main',
        handle: this.productHandle,
        variantId: variant.id,
        variant
      });
      
      window.eventBus.emit('VARIANT_CHANGED', {
        variant,
        sectionId: this.sectionId
      });
    }
  }

  /**
   * Update configurator section visibility
   * ONLY: Shows/hides sections based on transaction type
   */
  updateConfiguratorVisibility() {
    console.log('👁️ updateConfiguratorVisibility', { isRental: this.isRental });
    
    // Modules visibility (rental only)
    if (this.requiredModulesContainer) {
      if (this.isRental) {
        this.requiredModulesContainer.classList.remove('d-none');
        this.requiredModulesContainer.classList.add('d-block');
      } else {
        this.requiredModulesContainer.classList.add('d-none');
        this.requiredModulesContainer.classList.remove('d-block');
      }
      this.modulesRequired = this.isRental;
      
      if (!this.isRental) {
        this.requiredModuleCheckboxes?.forEach(cb => cb.checked = false);
      }
    }
    
    // Optional addons visibility
    const consumableSection = document.querySelector('.rr-optional-addons[data-addon-type="consumable"]');
    const optionalSection = document.querySelector('.rr-optional-addons[data-addon-type="optional"]');
    
    if (this.isRental) {
      // Show both consumables and optional equipment
      consumableSection?.classList.remove('d-none');
      consumableSection?.classList.add('d-block');
      optionalSection?.classList.remove('d-none');
      optionalSection?.classList.add('d-block');
    } else {
      // Keep consumables, hide optional equipment
      consumableSection?.classList.remove('d-none');
      consumableSection?.classList.add('d-block');
      optionalSection?.classList.add('d-none');
      optionalSection?.classList.remove('d-block');
      
      // Reset optional equipment quantities
      const addonItems = document.querySelectorAll('.rr-addon-item[data-type="optional"]');
      addonItems.forEach(item => {
        const quantityInput = document.querySelector(`[data-quantity-input="${item.dataset.id}"]`);
        if (quantityInput) quantityInput.value = 0;
      });
    }
    
    // Used equipment warning visibility (purchase only)
    if (this.usedEquipmentWarning) {
      if (this.isRental) {
        this.usedEquipmentWarning.style.display = 'none';
      } else {
        if (this.currentVariant) {
          this.updateUsedEquipmentWarning(this.currentVariant);
        }
      }
    }
    
    this.updateSelectedCount();

    // Refresh the zero-selected approval gate (init + transaction-type changes)
    if (this.zeroApprovalEnabled) {
      this.validateConfiguration();
    }
  }

  /**
   * Update configuration summary display
   * ONLY: Updates the summary UI with current selections
   */
  updateSummaryDisplay() {
    console.log('📊 updateSummaryDisplay', {
      isRental: this.isRental,
      currentVariant: this.currentVariant?.title,
      rentalDays: this.calculatedRentalDays
    });
    
    if (!this.summaryItems || !this.subtotalAmount) {
      console.warn('⚠️ Summary elements not found - cannot update');
      return;
    }
    
    // Check if required modules selection has changed and update URL
    this.checkAndUpdateURLForModuleChanges();
    
    // Toggle "no dates selected" state for rentals
    if (this.summaryContainer) {
      if (this.isRental && (!this.rentalStartDate || !this.rentalEndDate)) {
        this.summaryContainer.classList.add('rr-configuration-summary--no-dates');
      } else {
        this.summaryContainer.classList.remove('rr-configuration-summary--no-dates');
      }
    }
    
    const quantity = parseInt(this.quantityInput?.value) || 1;
    let totalPrice = 0;
    let itemsHTML = '';
    
    // Main product
    if (this.currentVariant) {
      const mainPrice = this.currentVariant.price / 100;
      const mainTotal = mainPrice * quantity;
      totalPrice += mainTotal;
      
      // Update main product display
      const mainQtyInput = document.querySelector('[data-summary-qty-input="main-product"]');
      if (mainQtyInput) {
        mainQtyInput.value = quantity;
      }
      
      const mainCalcText = document.getElementById(`main-product-calc-${this.sectionId}`);
      const mainPriceElement = document.getElementById(`summary-main-price-${this.sectionId}`);
      
      let priceLabel = '';
      // Only show days for rental mode with valid dates
      if (this.isRental && this.rentalStartDate && this.rentalEndDate && this.calculatedRentalDays > 0) {
        priceLabel = `for ${this.calculatedRentalDays} day${this.calculatedRentalDays > 1 ? 's' : ''}`;
      }
      
      const formattedMainPrice = window.formatMoney(Math.round(mainPrice * 100));
      const formattedMainTotal = window.formatMoney(Math.round(mainTotal * 100));
      
      if (mainCalcText) {
        mainCalcText.innerHTML = `× ${formattedMainPrice} ${priceLabel ? `<span class="rr-configuration-summary__price-period">${priceLabel}</span>` : ''}`;
      }
      
      if (mainPriceElement) {
        mainPriceElement.innerHTML = `${formattedMainTotal} ${priceLabel ? `<span class="rr-configuration-summary__price-period">${priceLabel}</span>` : ''}`;
      }
    }
    
    // Required modules (rental only)
    if (this.isRental) {
      const selectedModules = this.getSelectedRequiredModules();
      if (selectedModules.length > 0) {
        itemsHTML += '<div class="rr-configuration-summary__section-header">Required Modules</div>';
        
        for (const checkbox of selectedModules) {
          const itemHTML = this.buildSummaryItem(checkbox, quantity, false, false);
          if (itemHTML) {
            itemsHTML += itemHTML;
            totalPrice += this.getItemTotal(checkbox, quantity);
          }
        }
      }
    }
    
    // Optional addons
    const selectedAddons = this.getSelectedOptionalAddons();
    const consumables = selectedAddons.filter(item => item.dataset.type === 'consumable');
    const optionalEquipment = selectedAddons.filter(item => item.dataset.type !== 'consumable');
    
    if (consumables.length > 0) {
      itemsHTML += '<div class="rr-configuration-summary__section-header">Consumable Supplies</div>';
      for (const item of consumables) {
        const itemHTML = this.buildSummaryItem(item, 1, true);
        if (itemHTML) {
          itemsHTML += itemHTML;
          totalPrice += this.getItemTotal(item, 1, true);
        }
      }
    }
    
    if (this.isRental && optionalEquipment.length > 0) {
      itemsHTML += '<div class="rr-configuration-summary__section-header">Optional Equipment</div>';
      for (const item of optionalEquipment) {
        const itemHTML = this.buildSummaryItem(item, 1, true);
        if (itemHTML) {
          itemsHTML += itemHTML;
          totalPrice += this.getItemTotal(item, 1, true);
        }
      }
    }
    
    // Update DOM
    const mainProductItem = document.getElementById(`main-product-summary-item-${this.sectionId}`);
    if (mainProductItem) {
      const allItems = this.summaryItems.querySelectorAll('.rr-configuration-summary__item');
      allItems.forEach(item => {
        if (item.id !== `main-product-summary-item-${this.sectionId}`) {
          item.remove();
        }
      });
      this.summaryItems.querySelectorAll('.rr-configuration-summary__section-header').forEach(h => h.remove());
      
      if (itemsHTML) {
        mainProductItem.insertAdjacentHTML('afterend', itemsHTML);
      }
    }
    
    this.subtotalAmount.textContent = window.formatMoney(Math.round(totalPrice * 100));
    
    console.log('✅ updateSummaryDisplay completed', {
      finalTotalPrice: window.formatMoney(Math.round(totalPrice * 100))
    });
    
    this.bindSummaryQuantityControls();
  }

  /**
   * Set loading state for configurator
   * ONLY: Adds/removes loading classes and placeholder animations
   */
  setLoadingState(isLoading) {
    if (isLoading) {
      if (this.requiredModulesContainer) {
        this.requiredModulesContainer.classList.add('rr-loading');
      }
      if (this.optionalAddonsContainer) {
        this.optionalAddonsContainer.classList.add('rr-loading');
      }
      // Add placeholders to summary items
      this.setSummaryPlaceholders(true);
    } else {
      if (this.requiredModulesContainer) {
        this.requiredModulesContainer.classList.remove('rr-loading');
      }
      if (this.optionalAddonsContainer) {
        this.optionalAddonsContainer.classList.remove('rr-loading');
      }
      // Remove placeholders from summary items
      this.setSummaryPlaceholders(false);
    }
  }

  /**
   * Add/remove placeholder loading animation to summary items
   * ONLY: Toggles Bootstrap placeholder-glow classes
   */
  setSummaryPlaceholders(isLoading) {
    if (!this.summaryItems) return;
    
    // Main product price elements
    const mainCalcText = document.getElementById(`main-product-calc-${this.sectionId}`);
    const mainPriceElement = document.getElementById(`summary-main-price-${this.sectionId}`);
    
    if (isLoading) {
      // Add placeholder-glow animation
      if (mainCalcText) {
        mainCalcText.classList.add('placeholder-glow');
        mainCalcText.innerHTML = '<span class="placeholder col-8"></span>';
      }
      if (mainPriceElement) {
        mainPriceElement.classList.add('placeholder-glow');
        mainPriceElement.innerHTML = '<span class="placeholder col-6"></span>';
      }
      
      // Add placeholders to all summary item prices
      const itemPrices = this.summaryItems.querySelectorAll('.rr-configuration-summary__item-price');
      const itemCalcs = this.summaryItems.querySelectorAll('.rr-configuration-summary__calc-text');
      
      itemPrices.forEach(el => {
        el.classList.add('placeholder-glow');
        el.innerHTML = '<span class="placeholder col-5"></span>';
      });
      
      itemCalcs.forEach(el => {
        el.classList.add('placeholder-glow');
        el.innerHTML = '<span class="placeholder col-7"></span>';
      });
      
      // Subtotal
      if (this.subtotalAmount) {
        this.subtotalAmount.classList.add('placeholder-glow');
        this.subtotalAmount.innerHTML = '<span class="placeholder col-4"></span>';
      }
    } else {
      // Remove placeholder classes (content will be updated by updateSummaryDisplay)
      if (mainCalcText) {
        mainCalcText.classList.remove('placeholder-glow');
      }
      if (mainPriceElement) {
        mainPriceElement.classList.remove('placeholder-glow');
      }
      
      // Remove from all summary items
      const itemPrices = this.summaryItems.querySelectorAll('.rr-configuration-summary__item-price');
      const itemCalcs = this.summaryItems.querySelectorAll('.rr-configuration-summary__calc-text');
      
      itemPrices.forEach(el => el.classList.remove('placeholder-glow'));
      itemCalcs.forEach(el => el.classList.remove('placeholder-glow'));
      
      if (this.subtotalAmount) {
        this.subtotalAmount.classList.remove('placeholder-glow');
      }
    }
  }

  // ============================================================================
  // DOM EVENT BINDINGS
  // ============================================================================

  /**
   * Bind DOM event listeners (user interactions)
   */
  bindDOMEvents() {
    // Variant selects change
    this.variantSelects?.forEach(select => {
      select.addEventListener('change', () => this.handleVariantSelectionChanged());
    });

    // Required modules
    this.requiredModuleCheckboxes?.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleModuleChange());
    });

    // Zero-selected approval acknowledgement
    this.zeroApprovalCheckbox?.addEventListener('change', () => this.validateConfiguration());

    // Addon group toggles
    this.addonGroups?.forEach(group => {
      const header = group.querySelector('.rr-addon-group__header');
      header?.addEventListener('click', () => this.toggleAddonGroup(group));
    });

    // Accordion toggles for process accordions
    const accordionToggles = document.querySelectorAll('.rr-accordion--configurator [data-accordion-toggle]');
    accordionToggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const accordion = toggle.closest('.rr-accordion');
        if (accordion) {
          accordion.classList.toggle('collapsed');
        }
      });
    });
    
    // Quantity controls
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => this.updateSummaryDisplay());
      this.quantityInput.addEventListener('input', () => this.updateSummaryDisplay());
    }
    
    this.quantityButtons?.forEach(button => {
      button.addEventListener('click', (e) => this.handleQuantityButton(e, button));
    });

    // Addon quantity controls
    this.bindAddonQuantityControls();
    
    // Addon variant selects
    this.bindAddonVariantSelects();
    
    // Summary quantity controls
    this.bindSummaryQuantityControls();

    // Form submission
    this.productForm?.addEventListener('submit', (e) => this.handleFormSubmit(e));
  }

  /**
   * Handle module checkbox change
   */
  handleModuleChange() {
    console.log('🔄 handleModuleChange called');
    this.updateSelectedCount();
    this.validateConfiguration();
    this.updateSummaryDisplay();
  }

  /**
   * Handle quantity button click
   */
  handleQuantityButton(e, button) {
    e.preventDefault();
    
    if (!this.quantityInput) return;
    
    const currentValue = parseInt(this.quantityInput.value) || 1;

    if (button.name === 'plus') {
      this.quantityInput.value = currentValue + 1;
    } else if (button.name === 'minus' && currentValue > 1) {
      this.quantityInput.value = currentValue - 1;
    }
    
    this.updateSummaryDisplay();
  }

  /**
   * Bind addon quantity control event listeners
   */
  bindAddonQuantityControls() {
    const quantityButtons = document.querySelectorAll('[data-quantity-action]');
    
    quantityButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        
        const action = button.getAttribute('data-quantity-action');
        const addonId = button.getAttribute('data-addon-id');
        const input = document.querySelector(`[data-quantity-input="${addonId}"]`);
        
        if (!input) return;
        
        let currentValue = parseInt(input.value) || 0;
        
        if (action === 'increase') {
          currentValue++;
        } else if (action === 'decrease' && currentValue > 0) {
          currentValue--;
        }
        
        input.value = currentValue;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        
        this.toggleAddonActiveState(addonId, currentValue);
        this.updateSummaryDisplay();
      });
    });
  }

  /**
   * Bind variant select event listeners for consumables/optional items
   */
  bindAddonVariantSelects() {
    const variantSelects = document.querySelectorAll('.rr-addon-variant-select');
    
    console.log('🔍 Binding addon variant selects', { count: variantSelects.length });
    
    variantSelects.forEach(select => {
      // Initialize price from first selected variant
      this.initializeAddonVariantPrice(select);
      
      // Bind change listener
      select.addEventListener('change', (e) => {
        this.handleAddonVariantChange(e.target);
      });
    });
  }

  /**
   * Initialize price display from selected variant
   */
  initializeAddonVariantPrice(selectElement) {
    const productId = selectElement.dataset.productId;
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const price = parseInt(selectedOption.dataset.price) / 100;
    
    const priceElement = document.querySelector(`[data-price-for="${productId}"]`);
    if (priceElement) {
      const formattedPrice = window.formatMoney(Math.round(price * 100));
      priceElement.innerHTML = `${formattedPrice}<span class="rr-addon-item__price-label"> / per unit</span>`;
    }
  }

  /**
   * Handle addon variant selection change
   */
  handleAddonVariantChange(selectElement) {
    const productId = selectElement.dataset.productId;
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const price = parseInt(selectedOption.dataset.price) / 100;
    const option2 = selectedOption.dataset.option2;
    
    console.log('🔄 Addon variant changed', {
      productId,
      variantId: selectElement.value,
      option2,
      price: `$${price.toFixed(2)}`
    });
    
    const priceElement = document.querySelector(`[data-price-for="${productId}"]`);
    if (priceElement) {
      const formattedPrice = window.formatMoney(Math.round(price * 100));
      priceElement.innerHTML = `${formattedPrice}<span class="rr-addon-item__price-label"> / per unit</span>`;
    }
    
    this.updateSummaryDisplay();
  }

  /**
   * Bind summary quantity controls using event delegation
   */
  bindSummaryQuantityControls() {
    if (this.summaryQuantityBound) return;
    if (!this.summaryItems) return;
    
    this.summaryItems.addEventListener('click', (e) => {
      const button = e.target.closest('[data-summary-qty-action]');
      if (!button) return;
      
      e.stopPropagation();
      e.preventDefault();
      
      const action = button.getAttribute('data-summary-qty-action');
      const itemId = button.getAttribute('data-item-id');
      const summaryInput = document.querySelector(`[data-summary-qty-input="${itemId}"]`);
      
      if (!summaryInput) return;
      
      let currentValue = parseInt(summaryInput.value) || 1;
      const minValue = (itemId === 'main-product') ? 1 : 0;
      
      if (action === 'increase') {
        currentValue++;
      } else if (action === 'decrease' && currentValue > minValue) {
        currentValue--;
      }
      
      // Update appropriate input
      if (itemId === 'main-product') {
        const mainInput = this.productForm?.querySelector('[name="quantity"]');
        if (mainInput) {
          summaryInput.value = currentValue;
          mainInput.value = currentValue;
          mainInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        const cardInput = document.querySelector(`[data-quantity-input="${itemId}"]`);
        if (cardInput) {
          summaryInput.value = currentValue;
          cardInput.value = currentValue;
          cardInput.dispatchEvent(new Event('change', { bubbles: true }));
          
          this.toggleAddonActiveState(itemId, currentValue);
        }
      }
      
      this.updateSummaryDisplay();
    });
    
    this.summaryQuantityBound = true;
  }

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Toggle addon group open/closed
   */
  toggleAddonGroup(group) {
    group.classList.toggle('collapsed');
  }

  /**
   * Toggle active state class on addon items based on quantity
   */
  toggleAddonActiveState(addonId, quantity) {
    const addonItem = document.querySelector(`.rr-addon-item[data-id="${addonId}"]`);
    if (!addonItem) return;
    
    if (quantity > 0) {
      addonItem.classList.add('rr-addon-item--active');
    } else {
      addonItem.classList.remove('rr-addon-item--active');
    }
  }

  /**
   * Update selected module count
   */
  updateSelectedCount() {
    const selectedCount = this.getSelectedRequiredModules().length;
    const minSelections = parseInt(this.requiredModuleCount?.dataset.minSelections || 0);
    const maxSelections = parseInt(this.requiredModuleCount?.dataset.maxSelections || minSelections);

    if (this.selectedCountSpan) {
      this.selectedCountSpan.textContent = selectedCount;
    }

    // Check if requirements are satisfied
    const isInRange = selectedCount >= minSelections && selectedCount <= maxSelections;
    const requirementsSatisfied = minSelections > 0 && isInRange;

    // Toggle badge style between required (red) and complete (green)
    if (this.requiredModuleCount) {
      if (requirementsSatisfied) {
        this.requiredModuleCount.classList.remove('rr-accordion__badge--required');
        this.requiredModuleCount.classList.add('rr-accordion__badge--complete');
      } else {
        this.requiredModuleCount.classList.remove('rr-accordion__badge--complete');
        this.requiredModuleCount.classList.add('rr-accordion__badge--required');
      }
    }

    // Show/hide error
    if (this.requiredModuleError) {
      const shouldShowError = !isInRange && selectedCount > 0 && this.modulesRequired;

      if (shouldShowError) {
        this.requiredModuleError.classList.remove('d-none');
        this.requiredModuleError.classList.add('d-block');
      } else {
        this.requiredModuleError.classList.add('d-none');
        this.requiredModuleError.classList.remove('d-block');
      }
    }

    // Disable unchecked boxes if max reached
    this.requiredModuleCheckboxes?.forEach(checkbox => {
      if (!checkbox.checked && selectedCount >= maxSelections) {
        checkbox.disabled = true;
        checkbox.closest('.rr-module-option')?.classList.add('disabled');
      } else {
        checkbox.disabled = false;
        checkbox.closest('.rr-module-option')?.classList.remove('disabled');
      }
    });
  }

  /**
   * Update availability status
   */
  updateAvailability(variant) {
    if (!this.addToCartButton) return;

    if (variant.available) {
      const addText = this.addToCartButton.getAttribute('data-add-text') || 'Add to Cart';
      this.addToCartButton.querySelector('span').textContent = addText;
      
      if (this.availabilityStatus) {
        this.availabilityStatus.classList.remove('d-none');
        this.availabilityStatus.classList.add('d-flex');
      }
    } else {
      const soldOutText = this.addToCartButton.getAttribute('data-sold-out-text') || 'Sold Out';
      this.addToCartButton.querySelector('span').textContent = soldOutText;
      this.addToCartButton.disabled = true;
      
      if (this.availabilityStatus) {
        this.availabilityStatus.classList.add('d-none');
        this.availabilityStatus.classList.remove('d-flex');
      }
    }
  }

  /**
   * Update used equipment warning visibility
   */
  updateUsedEquipmentWarning(variant) {
    if (!this.usedEquipmentWarning) return;

    const opt1Lower = variant.option1?.toLowerCase() || '';
    const opt2Lower = variant.option2?.toLowerCase() || '';
    const isPurchase = opt1Lower === 'purchase';
    const isUsed = opt2Lower.includes('used');

    if (isPurchase && isUsed) {
      this.usedEquipmentWarning.style.display = 'block';
    } else {
      this.usedEquipmentWarning.style.display = 'none';
    }
  }

  /**
   * Get selected options from all variant selects
   */
  getSelectedOptions() {
    const selectedOptions = [];
    this.variantSelects?.forEach(select => {
      selectedOptions.push(select.value);
    });
    return selectedOptions;
  }

  /**
   * Find variant matching the selected options
   */
  findMatchingVariant(selectedOptions) {
    return this.variants.find(variant => {
      if (!variant.options || variant.options.length !== selectedOptions.length) {
        return false;
      }
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }

  /**
   * Get selected required modules
   */
  getSelectedRequiredModules() {
    return Array.from(this.requiredModuleCheckboxes || []).filter(cb => cb.checked);
  }

  /**
   * Get all available module types from required modules
   */
  getAvailableModuleTypes() {
    const types = new Set();
    Array.from(this.requiredModuleCheckboxes || []).forEach(cb => {
      const moduleType = cb.dataset.moduleType;
      if (moduleType && moduleType !== 'Module') {
        types.add(moduleType);
      }
    });
    return Array.from(types);
  }

  /**
   * Get selected module types
   */
  getSelectedModuleTypes() {
    const types = new Set();
    this.getSelectedRequiredModules().forEach(cb => {
      const moduleType = cb.dataset.moduleType;
      if (moduleType && moduleType !== 'Module') {
        types.add(moduleType);
      }
    });
    return Array.from(types);
  }

  /**
   * Get selected optional addons (quantity > 0)
   */
  getSelectedOptionalAddons() {
    const addonItems = document.querySelectorAll('.rr-addon-item[data-type]');
    return Array.from(addonItems).filter(item => {
      const quantityInput = document.querySelector(`[data-quantity-input="${item.dataset.id}"]`);
      const quantity = parseInt(quantityInput?.value || 0);
      return quantity > 0;
    });
  }

  /**
   * Build summary item HTML
   */
  buildSummaryItem(element, baseQuantity, useOwnQuantity = false, showQuantitySelector = true) {
    const itemId = element.dataset.id || element.dataset.moduleId || element.dataset.addonId;
    const itemName = element.dataset.name;
    const priceElement = document.querySelector(`[data-price-for="${itemId}"]`);
    
    if (!priceElement || priceElement.textContent === 'Loading...') return null;
    
    // Extract price more carefully - only get the first monetary value
    const priceMatch = priceElement.textContent.match(/[\d,]+\.?\d*/);
    const priceText = priceMatch ? priceMatch[0].replace(/,/g, '') : '0';
    const unitPrice = parseFloat(priceText) || 0;
    
    const itemQuantity = useOwnQuantity 
      ? parseInt(document.querySelector(`[data-quantity-input="${itemId}"]`)?.value || 1)
      : baseQuantity;
    
    const lineTotal = unitPrice * itemQuantity;
    
    let period = '';
    // Only show period for non-consumables in rental mode with valid dates
    if (element.dataset.type !== 'consumable' && this.isRental && this.rentalStartDate && this.rentalEndDate && this.calculatedRentalDays > 0) {
      period = `for ${this.calculatedRentalDays} day${this.calculatedRentalDays > 1 ? 's' : ''}`;
    }
    
    const periodLabel = element.dataset.type === 'consumable' ? '/ per unit' : period;
    
    const formattedUnitPrice = window.formatMoney(Math.round(unitPrice * 100));
    const formattedLineTotal = window.formatMoney(Math.round(lineTotal * 100));
    
    // Build quantity display based on showQuantitySelector flag
    let quantityHTML = '';
    if (showQuantitySelector) {
      quantityHTML = `
        <div class="rr-qty-selector rr-qty-selector--sm">
          <button type="button" class="rr-qty-btn" data-summary-qty-action="decrease" data-item-id="${itemId}">−</button>
          <input type="number" class="rr-qty-input" data-summary-qty-input="${itemId}" value="${itemQuantity}" min="0" readonly>
          <button type="button" class="rr-qty-btn" data-summary-qty-action="increase" data-item-id="${itemId}">+</button>
        </div>
      `;
    } else {
      quantityHTML = `
        <span class="rr-configuration-summary__item-qty" data-summary-qty-display="${itemId}">${itemQuantity}×</span>
      `;
    }
    
    // Get variant information for consumables with variant selectors
    let variantHTML = '';
    if (element.dataset.type === 'consumable') {
      const variantSelect = document.querySelector(`[data-variant-select="${itemId}"]`);
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantName = selectedOption?.dataset.option2 || selectedOption?.textContent;
        if (variantName) {
          variantHTML = `<span class="rr-configuration-summary__item-variant">${variantName}</span>`;
        }
      }
    }
    
    return `
      <div class="rr-configuration-summary__item">
        <div class="rr-configuration-summary__item-info">
          <span class="rr-configuration-summary__item-name">${itemName}</span>
          ${variantHTML}
          <div class="rr-configuration-summary__item-calc">
            ${quantityHTML}
            <span class="rr-configuration-summary__calc-text">${formattedUnitPrice} ${periodLabel}</span>
          </div>
        </div>
        <span class="rr-configuration-summary__item-price">${formattedLineTotal}${period ? ` <span class="rr-configuration-summary__price-period">${period}</span>` : ''}</span>
      </div>
    `;
  }

  /**
   * Get item total price
   */
  getItemTotal(element, baseQuantity, useOwnQuantity = false) {
    const itemId = element.dataset.id || element.dataset.moduleId || element.dataset.addonId;
    const priceElement = document.querySelector(`[data-price-for="${itemId}"]`);
    
    if (!priceElement || priceElement.textContent === 'Loading...') return 0;
    
    // Extract price more carefully - only get the first monetary value
    const priceMatch = priceElement.textContent.match(/[\d,]+\.?\d*/);
    const priceText = priceMatch ? priceMatch[0].replace(/,/g, '') : '0';
    const unitPrice = parseFloat(priceText) || 0;
    
    const itemQuantity = useOwnQuantity 
      ? parseInt(document.querySelector(`[data-quantity-input="${itemId}"]`)?.value || 1)
      : baseQuantity;
    
    return unitPrice * itemQuantity;
  }

  /**
   * Determine whether zero-selected approval applies right now.
   * Only for rentals where required modules are optional (min 0) and none are selected.
   */
  shouldRequireZeroApproval() {
    if (!this.zeroApprovalEnabled || !this.isRental || !this.modulesRequired) {
      return false;
    }
    const minSelections = parseInt(this.requiredModuleCount?.dataset.minSelections || 0);
    if (minSelections > 0) {
      return false;
    }
    return this.getSelectedRequiredModules().length === 0;
  }

  /**
   * Show/hide the inline acknowledgement and reset it when not applicable.
   * Returns whether acknowledgement is currently required.
   */
  syncZeroApprovalUI() {
    if (!this.zeroApprovalContainer) {
      return false;
    }

    const applies = this.shouldRequireZeroApproval();

    if (applies) {
      this.zeroApprovalContainer.classList.remove('d-none');
      this.zeroApprovalContainer.classList.add('d-block');
    } else {
      this.zeroApprovalContainer.classList.add('d-none');
      this.zeroApprovalContainer.classList.remove('d-block');
      if (this.zeroApprovalCheckbox) {
        this.zeroApprovalCheckbox.checked = false;
      }
    }

    return applies;
  }

  /**
   * Validate configuration
   */
  validateConfiguration() {
    let isValid = true;
    let validationMessage = '';

    // Check variant availability
    if (this.currentVariant && !this.currentVariant.available) {
      isValid = false;
      validationMessage = 'This item is currently unavailable';
    }

    // Check required modules if rental
    if (this.isRental && this.requiredModulesContainer && this.modulesRequired) {
      const minSelections = parseInt(this.requiredModuleCount?.dataset.minSelections || 0);
      const maxSelections = parseInt(this.requiredModuleCount?.dataset.maxSelections || minSelections);
      const maxLabel = maxSelections > 999 ? 'unlimited' : maxSelections;
      const selectedCount = this.getSelectedRequiredModules().length;
      
      if (minSelections > 0 && (selectedCount < minSelections || selectedCount > maxSelections)) {
        isValid = false;
        
        // Get unique module types that are NOT selected
        const allModuleTypes = this.getAvailableModuleTypes();
        const selectedModuleTypes = this.getSelectedModuleTypes();
        const unselectedTypes = allModuleTypes.filter(type => !selectedModuleTypes.includes(type));
        
        // Build module type descriptor
        let moduleTypeText = 'module';
        if (unselectedTypes.length > 0) {
          moduleTypeText = unselectedTypes.join(' or ');
        } else if (allModuleTypes.length > 0) {
          moduleTypeText = allModuleTypes.join(' or ');
        }
        
        // Build specific message based on selection state
        if (selectedCount === 0) {
          if (minSelections === maxSelections) {
            validationMessage = `Please select ${minSelections} ${moduleTypeText}${minSelections > 1 ? 's' : ''} to continue`;
          } else {
            validationMessage = `Please select ${minSelections}-${maxLabel} ${moduleTypeText}s to continue`;
          }
        } else if (selectedCount < minSelections) {
          const remaining = minSelections - selectedCount;
          validationMessage = `Select ${remaining} more ${moduleTypeText}${remaining > 1 ? 's' : ''} (${selectedCount}/${minSelections} selected)`;
        } else if (selectedCount > maxSelections) {
          const excess = selectedCount - maxSelections;
          validationMessage = `Too many modules selected. Remove ${excess} (${selectedCount}/${maxSelections} maximum)`;
        }
      }
    }

    // Zero-selected approval: require explicit acknowledgement before adding to cart
    // when no required modules are selected. The checkbox label is the prompt, so no
    // separate validation message is shown here.
    if (this.syncZeroApprovalUI() && !this.zeroApprovalCheckbox?.checked) {
      isValid = false;
    }

    // Update validation message display
    // Only reveal the box when there is actual message text. Some invalid states
    // (e.g. the zero-selected approval gate) rely on their own inline UI instead.
    if (this.validationMessage) {
      if (isValid || !validationMessage) {
        this.validationMessage.classList.add('d-none');
        this.validationMessage.classList.remove('d-block');
        this.validationMessage.textContent = '';
      } else {
        this.validationMessage.classList.remove('d-none');
        this.validationMessage.classList.add('d-block');
        this.validationMessage.textContent = validationMessage;
      }
    }

    // Update add to cart button
    if (this.addToCartButton) {
      this.addToCartButton.disabled = !isValid;
      this.addToCartButton.style.opacity = isValid ? '1' : '0.5';
      this.addToCartButton.style.cursor = isValid ? 'pointer' : 'not-allowed';
    }

    return isValid;
  }

  // ============================================================================
  // TRANSACTION TYPE MANAGEMENT
  // ============================================================================

  /**
   * Initialize transaction type availability
   */
  initializeTransactionTypes() {
    const availableTypes = this.getAvailableTransactionTypes();

    const rentRadio = document.getElementById(`transaction-rent-${this.sectionId}`);
    const purchaseRadio = document.getElementById(`transaction-purchase-${this.sectionId}`);

    // Disable unavailable options and add cursor styling
    if (!availableTypes.rent && rentRadio) {
      rentRadio.disabled = true;
      const rentLabel = rentRadio.nextElementSibling;
      if (rentLabel) {
        rentLabel.style.cursor = 'not-allowed';
      }
    }

    if (!availableTypes.purchase && purchaseRadio) {
      purchaseRadio.disabled = true;
      const purchaseLabel = document.getElementById(`purchase-label-${this.sectionId}`);
      if (purchaseLabel) {
        purchaseLabel.style.cursor = 'not-allowed';
      }
    }

    // Set initial transaction type
    if (availableTypes.rent && !availableTypes.purchase) {
      this.currentTransactionType = 'Rent';
      if (rentRadio) rentRadio.checked = true;
    } else if (availableTypes.purchase && !availableTypes.rent) {
      this.currentTransactionType = 'Purchase';
      if (purchaseRadio) purchaseRadio.checked = true;
    }

    this.updateOptionsForTransactionType(this.currentTransactionType);
    
    // Note: Bootstrap tooltips are initialized separately after DOM is fully ready
    // See bottom of file for initializeTransactionTooltips() call
  }
  
  /**
   * Initialize Bootstrap tooltips for disabled transaction type buttons
   * Uses wrapper divs around disabled elements for proper tooltip triggering
   * Non-blocking with max retry limit to prevent performance impact
   */
  initializeTransactionTooltips(retryCount = 0) {
    const MAX_RETRIES = 10; // Stop after 1 second total (10 * 100ms)
    
    // Check Bootstrap availability
    if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) {
      if (retryCount < MAX_RETRIES) {
        // Non-blocking retry - won't impact page load
        setTimeout(() => this.initializeTransactionTooltips(retryCount + 1), 100);
      } else {
        console.warn('⚠️ Bootstrap tooltips not initialized (Bootstrap not loaded after 1s)');
      }
      return;
    }
    
    // Find all tooltip wrappers (preview + configurator)
    const tooltipWrappers = document.querySelectorAll('.rr-tooltip-wrapper[data-bs-toggle="tooltip"]');
    
    if (tooltipWrappers.length === 0) {
      console.log('ℹ️ No disabled transaction buttons found (all options available)');
      return;
    }
    
    // Initialize tooltips with consistent configuration
    tooltipWrappers.forEach(wrapper => {
      try {
        new bootstrap.Tooltip(wrapper, {
          trigger: 'hover focus',
          placement: 'top'
        });
      } catch (error) {
        console.error('❌ Tooltip initialization failed:', error);
      }
    });
    
    console.log('✅ Transaction tooltips initialized:', tooltipWrappers.length);
  }

  /**
   * Determine available transaction types from variants
   */
  getAvailableTransactionTypes() {
    const types = { rent: false, purchase: false };

    this.variants.forEach(variant => {
      if (!variant.option1) return;
      const opt1Lower = variant.option1.toLowerCase();
      
      if (opt1Lower === 'rent') {
        types.rent = true;
      } else if (opt1Lower === 'purchase') {
        types.purchase = true;
      }
    });

    return types;
  }

  /**
   * Update available options based on transaction type
   */
  updateOptionsForTransactionType(transactionType) {
    this.currentTransactionType = transactionType;

    if (!this.variantSelects || this.variantSelects.length === 0) return;

    // Hide the first select (using radio buttons instead)
    const firstSelect = this.variantSelects[0];
    const firstOptionContainer = document.getElementById(`option-0-${this.sectionId}`);
    if (firstOptionContainer) {
      firstOptionContainer.classList.add('d-none');
    }

    // Update first select value
    if (firstSelect) {
      firstSelect.value = transactionType;
    }

    // Get second select (Condition for Purchase, Duration for Rent)
    const secondSelect = this.variantSelects[1];
    if (!secondSelect) return;

    const secondOptionContainer = document.getElementById(`option-1-${this.sectionId}`);
    const isRent = transactionType === 'Rent';

    // Hide duration selector for rentals (dates drive selection)
    if (secondOptionContainer) {
      if (isRent) {
        secondOptionContainer.classList.add('d-none');
        secondOptionContainer.classList.remove('d-block');
      } else {
        secondOptionContainer.classList.remove('d-none');
        secondOptionContainer.classList.add('d-block');
      }
    }

    // Populate second select options
    const availableValues = new Set();
    this.variants.forEach(variant => {
      if (!variant.option1 || !variant.option2) return;
      const opt1Lower = variant.option1.toLowerCase();
      
      if ((isRent && opt1Lower === 'rent') || (!isRent && opt1Lower === 'purchase')) {
        availableValues.add(variant.option2);
      }
    });

    secondSelect.innerHTML = '';
    const valuesArray = Array.from(availableValues);
    
    valuesArray.forEach(value => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      secondSelect.appendChild(option);
    });

    // Select first available value
    if (valuesArray.length > 0) {
      secondSelect.value = valuesArray[0];
    }

    // Trigger variant update
    const selectedOptions = this.getSelectedOptions();
    const matchingVariant = this.findMatchingVariant(selectedOptions);
    
    if (matchingVariant) {
      console.log('✅ Found matching variant for transaction type:', transactionType, matchingVariant.title);
      this.currentVariant = matchingVariant;
      this.updateMainProductDisplay();
    } else {
      console.error('❌ No matching variant found for transaction type:', transactionType);
      
      // Fallback: try to find ANY variant with the correct transaction type
      const fallbackVariant = this.variants.find(v => {
        const opt1Lower = v.option1?.toLowerCase() || '';
        return (isRent && opt1Lower === 'rent') || (!isRent && opt1Lower === 'purchase');
      });
      
      if (fallbackVariant) {
        console.warn('⚠️ Using fallback variant:', fallbackVariant.title);
        if (secondSelect) {
          secondSelect.value = fallbackVariant.option2;
        }
        this.currentVariant = fallbackVariant;
        this.updateMainProductDisplay();
      } else {
        console.error('❌ No fallback variant found either!');
      }
    }
  }

  /**
   * Update product info (LEGACY - used during initialization)
   * TODO: Refactor to use updateMainProductDisplay instead
   */
  updateProductInfo(variant, skipPriceUpdate = false) {
    if (!variant) {
      console.error('❌ updateProductInfo called with no variant!');
      return;
    }

    console.log('💰 updateProductInfo (LEGACY)', {
      variant: variant.title,
      skipPriceUpdate
    });

    this.currentVariant = variant;
    this.updateMainProductDisplay();
    
    if (!skipPriceUpdate) {
      this.validateConfiguration();
      this.updateSummaryDisplay();
    }
  }

  // ============================================================================
  // RENTAL DATES MANAGEMENT
  // ============================================================================

  /**
   * Reload rental dates if they exist (when switching back to RENT mode)
   * Checks cart attributes first, then tries to extract from date picker UI
   */
  async reloadRentalDatesIfNeeded() {
    console.log('🔄 reloadRentalDatesIfNeeded - checking for existing dates');
    
    // Try to get dates from cart attributes
    try {
      const cart = window.CartManager 
        ? await CartManager.getCart()
        : await (await fetch('/cart.js')).json();
      
      const startDate = cart.attributes?.['Rental Start Date'];
      const endDate = cart.attributes?.['Rental End Date'];
      
      if (startDate && endDate) {
        console.log('✅ Found dates in cart attributes:', startDate, endDate);
        
        // Calculate days
        const days = window.VariantSelector 
          ? VariantSelector.calculateDays(startDate, endDate)
          : 0;
        
        // Store dates
        this.rentalStartDate = startDate;
        this.rentalEndDate = endDate;
        this.calculatedRentalDays = days;
        
        console.log(`✅ Reloaded ${days} days from cart`);
        return;
      }
    } catch (error) {
      console.error('❌ Error loading dates from cart:', error);
    }
    
    // Try to extract dates from date picker UI (if visible)
    try {
      const rentalPeriodElement = document.querySelector('.rental-period-display, [data-rental-period]');
      if (rentalPeriodElement) {
        const startDateInput = document.querySelector('input[name="rental_start_date"], [data-start-date]');
        const endDateInput = document.querySelector('input[name="rental_end_date"], [data-end-date]');
        
        if (startDateInput?.value && endDateInput?.value) {
          const startDate = startDateInput.value;
          const endDate = endDateInput.value;
          
          console.log('✅ Found dates in date picker UI:', startDate, endDate);
          
          // Calculate days
          const days = window.VariantSelector 
            ? VariantSelector.calculateDays(startDate, endDate)
            : 0;
          
          // Store dates
          this.rentalStartDate = startDate;
          this.rentalEndDate = endDate;
          this.calculatedRentalDays = days;
          
          console.log(`✅ Reloaded ${days} days from date picker`);
          return;
        }
      }
    } catch (error) {
      console.error('❌ Error extracting dates from UI:', error);
    }
    
    console.log('ℹ️ No existing dates found - will use 1-day pricing');
  }

  /**
   * Load rental dates from cart on page load
   */
  async loadDatesFromCart() {
    console.log('🛒 Loading dates from cart');
    
    try {
      const cart = window.CartManager 
        ? await CartManager.getCart()
        : await (await fetch('/cart.js')).json();
      
      const startDate = cart.attributes?.['Rental Start Date'];
      const endDate = cart.attributes?.['Rental End Date'];
      
      if (startDate && endDate) {
        console.log('✅ Found dates in cart:', startDate, endDate);
        if (this.isRental) {
          await this.setRentalDates(startDate, endDate);
        } else {
          console.log('ℹ️ Skipping dates - product is purchase-only');
        }
      } else {
        console.log('ℹ️ No dates in cart - showing 1-day rates');
        if (this.isRental) {
          await this.updateRentalItemPrices(1);
          this.updateSummaryDisplay();
        }
      }
    } catch (error) {
      console.error('❌ Error loading dates from cart:', error);
    }
  }

  /**
   * Set rental dates
   * Emits DATES_CHANGED event
   */
  async setRentalDates(startDate, endDate) {
    console.log('📅 setRentalDates:', startDate, endDate);
    
    // Clear dates
    if (!startDate && !endDate) {
      this.rentalStartDate = null;
      this.rentalEndDate = null;
      this.calculatedRentalDays = null;
      this.updateSummaryDisplay();
      return;
    }
    
    // Validate
    if (!startDate || !endDate) {
      console.error('❌ Invalid dates');
      return;
    }
    
    // Store dates
    this.rentalStartDate = startDate;
    this.rentalEndDate = endDate;
    
    // Calculate days
    const days = window.VariantSelector 
      ? VariantSelector.calculateDays(startDate, endDate)
      : 0;
    
    this.calculatedRentalDays = days;
    console.log(`📊 Calculated ${days} days`);
    
    // Select variant for rental mode
    if (this.isRental && this.productHandle && window.VariantSelector) {
      const variant = await VariantSelector.selectVariantForDates(
        this.productHandle,
        startDate,
        endDate
      );
      
      if (variant) {
        const secondSelect = this.variantSelects?.[1];
        if (secondSelect) {
          secondSelect.value = variant.option2;
        }
        this.currentVariant = variant;
        this.updateMainProductDisplay();
      }
    }
    
    // Update prices and summary
    await this.updateRentalItemPrices(days);
    this.updateSummaryDisplay();
    
    // Emit event
    if (window.eventBus) {
      window.eventBus.emit('DATES_CHANGED', { startDate, endDate, days });
    }
  }

  // ============================================================================
  // URL STATE MANAGEMENT
  // ============================================================================

  /**
   * Load main product quantity from URL query parameter
   * Reads `rr_qty` and pre-populates the main quantity input.
   * Example: /products/handle?rr_qty=5
   */
  loadMainQuantityFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const rrQty = urlParams.get('rr_qty');

    if (!rrQty) return;

    const quantity = parseInt(rrQty, 10);

    if (isNaN(quantity) || quantity < 1) {
      console.warn('⚠️ Invalid rr_qty value in URL:', rrQty);
      return;
    }

    if (!this.quantityInput) {
      console.warn('⚠️ Cannot apply rr_qty - quantity input not found');
      return;
    }

    console.log('🔗 Loading main quantity from URL', { quantity });

    this.quantityInput.value = quantity;
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Load required modules from URL query parameter
   */
  loadRequiredModulesFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const rrReq = urlParams.get('rr_req');
    
    if (!rrReq) return;
    
    const moduleIds = rrReq.split(',').map(id => id.trim()).filter(id => id);
    
    if (moduleIds.length === 0) return;
    
    console.log('🔗 Loading required modules from URL', { ids: moduleIds });
    
    this.requiredModuleCheckboxes?.forEach(checkbox => {
      const id = checkbox.dataset.id;
      if (id && moduleIds.includes(id)) {
        checkbox.checked = true;
      }
    });
    
    this.updateSelectedCount();
    this.validateConfiguration();
  }
  
  /**
   * Load consumables from URL query parameter
   */
  loadConsumablesFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const rrConsumable = urlParams.get('rr_consumable');
    
    if (!rrConsumable) return;
    
    const consumableEntries = rrConsumable.split(',').map(entry => {
      const parts = entry.split(':').map(s => s.trim());
      return { 
        id: parts[0],
        variantId: parts[1] || null,
        qty: parseInt(parts[2]) || 1
      };
    }).filter(e => e.id);
    
    if (consumableEntries.length === 0) return;
    
    console.log('🔗 Loading consumables from URL', { entries: consumableEntries });
    
    consumableEntries.forEach(({ id, variantId, qty }) => {
      if (variantId) {
        const variantSelect = document.querySelector(`[data-variant-select="${id}"]`);
        if (variantSelect) {
          variantSelect.value = variantId;
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      
      const quantityInput = document.querySelector(`[data-quantity-input="${id}"]`);
      if (quantityInput) {
        quantityInput.value = qty;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      this.toggleAddonActiveState(id, qty);
    });
  }
  
  /**
   * Load optional modules from URL query parameter
   */
  loadOptionalModulesFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const rrOptional = urlParams.get('rr_optional');
    
    if (!rrOptional) return;
    
    const optionalEntries = rrOptional.split(',').map(entry => {
      const parts = entry.split(':').map(s => s.trim());
      return { 
        id: parts[0],
        variantId: parts[1] || null,
        qty: parseInt(parts[2]) || 1
      };
    }).filter(e => e.id);
    
    if (optionalEntries.length === 0) return;
    
    console.log('🔗 Loading optional modules from URL', { entries: optionalEntries });
    
    optionalEntries.forEach(({ id, variantId, qty }) => {
      if (variantId) {
        const variantSelect = document.querySelector(`[data-variant-select="${id}"]`);
        if (variantSelect) {
          variantSelect.value = variantId;
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      
      const quantityInput = document.querySelector(`[data-quantity-input="${id}"]`);
      if (quantityInput) {
        quantityInput.value = qty;
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      this.toggleAddonActiveState(id, qty);
    });
  }

  /**
   * Check if module selections have changed and update URL
   */
  checkAndUpdateURLForModuleChanges() {
    const selectedModules = this.getSelectedRequiredModules();
    const currentIds = selectedModules
      .map(cb => cb.dataset.id)
      .filter(id => id)
      .join(',');
    
    const currentConsumables = this.getConsumablesState();
    const currentOptional = this.getOptionalModulesState();
    
    let hasChanges = false;
    
    if (currentIds !== this.previousRequiredModulesIds) {
      console.log('🔗 Required modules changed', {
        previous: this.previousRequiredModulesIds || '(none)',
        current: currentIds || '(none)'
      });
      this.previousRequiredModulesIds = currentIds;
      hasChanges = true;
    }
    
    if (currentConsumables !== this.previousConsumablesState) {
      console.log('🔗 Consumables changed', {
        previous: this.previousConsumablesState || '(none)',
        current: currentConsumables || '(none)'
      });
      this.previousConsumablesState = currentConsumables;
      hasChanges = true;
    }
    
    if (currentOptional !== this.previousOptionalModulesState) {
      console.log('🔗 Optional modules changed', {
        previous: this.previousOptionalModulesState || '(none)',
        current: currentOptional || '(none)'
      });
      this.previousOptionalModulesState = currentOptional;
      hasChanges = true;
    }
    
    if (hasChanges) {
      this.updateURLWithAllSelections(currentIds, currentConsumables, currentOptional);
    }
  }
  
  /**
   * Get consumables state as string
   */
  getConsumablesState() {
    const consumableItems = document.querySelectorAll('.rr-addon-item[data-type="consumable"]');
    const state = Array.from(consumableItems)
      .map(item => {
        const id = item.dataset.id;
        const quantityInput = document.querySelector(`[data-quantity-input="${id}"]`);
        const qty = parseInt(quantityInput?.value || 0);
        
        if (qty === 0) return null;
        
        const variantSelect = document.querySelector(`[data-variant-select="${id}"]`);
        const variantId = variantSelect?.value || '';
        
        return variantId ? `${id}:${variantId}:${qty}` : `${id}::${qty}`;
      })
      .filter(s => s)
      .join(',');
    
    return state;
  }
  
  /**
   * Get optional modules state as string
   */
  getOptionalModulesState() {
    const optionalItems = document.querySelectorAll('.rr-addon-item[data-type="optional"]');
    const state = Array.from(optionalItems)
      .map(item => {
        const id = item.dataset.id;
        const quantityInput = document.querySelector(`[data-quantity-input="${id}"]`);
        const qty = parseInt(quantityInput?.value || 0);
        
        if (qty === 0) return null;
        
        const variantSelect = document.querySelector(`[data-variant-select="${id}"]`);
        const variantId = variantSelect?.value || '';
        
        return variantId ? `${id}:${variantId}:${qty}` : `${id}::${qty}`;
      })
      .filter(s => s)
      .join(',');
    
    return state;
  }

  /**
   * Update URL with all selections
   */
  updateURLWithAllSelections(requiredIds, consumablesState, optionalState) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    
    if (requiredIds) {
      params.set('rr_req', requiredIds);
    } else {
      params.delete('rr_req');
    }
    
    if (consumablesState) {
      params.set('rr_consumable', consumablesState);
    } else {
      params.delete('rr_consumable');
    }
    
    if (optionalState) {
      params.set('rr_optional', optionalState);
    } else {
      params.delete('rr_optional');
    }
    
    const newSearch = params.toString();
    const newURL = newSearch 
      ? `${url.pathname}?${newSearch}${url.hash}`
      : `${url.pathname}${url.hash}`;
    
    window.history.replaceState({}, '', newURL);
    
    console.log('✅ URL updated', {
      rr_req: requiredIds || '(none)',
      rr_consumable: consumablesState || '(none)',
      rr_optional: optionalState || '(none)',
      url: newURL
    });
  }

  // ============================================================================
  // FORM SUBMISSION
  // ============================================================================

  /**
   * Handle form submission
   */
  async handleFormSubmit(event) {
    event.preventDefault();

    // Validate rental dates
    if (this.isRental && (!this.rentalStartDate || !this.rentalEndDate)) {
      alert('Please select rental dates before adding to cart.');
      return;
    }

    // Validate configuration
    if (!this.validateConfiguration()) {
      this.showValidationError();
      return;
    }

    // Apply loading state
    const originalText = this.addToCartButton?.querySelector('span')?.textContent;
    if (this.addToCartButton) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.add('rr-loading');
      this.addToCartButton.querySelector('span').textContent = 'Adding to Cart...';
    }
    
    if (this.productForm) {
      this.productForm.classList.add('rr-loading');
    }

    try {
      // Get form data
      const formData = new FormData(this.productForm);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity')) || 1;

      // Collect properties
      const properties = this.buildLineItemProperties(formData);
      
      // Add main product
      if (window.CartManager) {
        await CartManager.addItem(variantId, quantity, properties);
      } else {
        await this.addToCartFallback(variantId, quantity, properties);
      }

      // Get duration for modules/addons
      const duration = this.currentVariant?.option2 || null;
      
      // Add required modules (rental only)
      if (this.isRental && duration) {
        for (const checkbox of this.getSelectedRequiredModules()) {
          await this.addModuleToCart(checkbox, quantity, duration, variantId, 'required');
        }
      }

      // Add optional addons (rental only) and consumables
      for (const item of this.getSelectedOptionalAddons()) {
        const itemType = item.dataset.type;
        const itemQuantity = parseInt(document.querySelector(`[data-quantity-input="${item.dataset.id}"]`)?.value || 1);
        
        if (itemType === 'consumable') {
          await this.addModuleToCart(item, itemQuantity, 'Purchase', variantId, 'consumable');
        } else if (this.isRental && duration) {
          await this.addModuleToCart(item, itemQuantity, duration, variantId, 'optional');
        }
      }
      
      // Update cart attributes with rental dates
      if (this.isRental && this.rentalStartDate && this.rentalEndDate) {
        const days = this.calculatedRentalDays || 
          (window.VariantSelector ? VariantSelector.calculateDays(this.rentalStartDate, this.rentalEndDate) : 0);
        
        if (window.CartManager) {
          await CartManager.updateAttributes({
            'Rental Start Date': this.rentalStartDate,
            'Rental End Date': this.rentalEndDate,
            'Rental Duration (Days)': days.toString()
          });
        }
      }

      // Success! Redirect to cart
      window.location.href = '/cart';
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('There was an error adding items to cart. Please try again.');

      // Remove loading states and re-enable button
      if (this.addToCartButton && originalText) {
        this.addToCartButton.disabled = false;
        this.addToCartButton.classList.remove('rr-loading');
        this.addToCartButton.querySelector('span').textContent = originalText;
        this.validateConfiguration();
      }
      
      if (this.productForm) {
        this.productForm.classList.remove('rr-loading');
      }
    }
  }

  /**
   * Build line item properties from form data
   */
  buildLineItemProperties(formData) {
    const properties = {};
    
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.match(/properties\[(.*?)\]/)[1];
        // If property already exists (e.g., multiple "Required Module" checkboxes), combine with ~~
        if (properties[propName]) {
          properties[propName] += `~~${value}`;
        } else {
          properties[propName] = value;
        }
      }
    }
    
    // Add rental dates and duration
    if (this.isRental && this.rentalStartDate && this.rentalEndDate) {
      const days = this.calculatedRentalDays || 
        (window.VariantSelector ? VariantSelector.calculateDays(this.rentalStartDate, this.rentalEndDate) : 0);
      
      properties['Rental Start Date'] = this.rentalStartDate;
      properties['Rental End Date'] = this.rentalEndDate;
      properties['Rental Duration (Days)'] = days;
    }
    
    return properties;
  }

  /**
   * Add module/addon to cart
   */
  async addModuleToCart(element, quantity, duration, mainProductVariantId, moduleType) {
    const handle = element.dataset.handle || element.dataset.moduleHandle || element.dataset.addonHandle;
    const productId = element.dataset.id || element.dataset.addonId;
    
    // Check if there's a variant selector for this item
    const variantSelect = document.querySelector(`[data-variant-select="${productId}"]`);
    let variantId;
    
    if (variantSelect && variantSelect.value) {
      variantId = parseInt(variantSelect.value);
      console.log(`✅ Using selected variant from dropdown: ${variantId}`);
    } else if (window.ProductDataCache && window.VariantSelector) {
      const productData = await ProductDataCache.get(handle);
      const variant = productData.variants.find(v =>
        (v.option1?.toLowerCase() === 'rent' && v.option2 === duration) ||
        (v.option1 === duration) ||
        (v.option1?.toLowerCase() === 'purchase' && duration === 'Purchase')
      );
      variantId = variant?.id;
    }
    
    if (!variantId) {
      console.warn('⚠️ No variant ID found for item:', { productId, handle });
      return;
    }
    
    // Build properties
    const properties = {
      _related_to: mainProductVariantId,
      _module_type: moduleType,
      _rental_duration: duration
    };
    
    // Add rental dates for non-consumables
    if (moduleType !== 'consumable' && this.rentalStartDate && this.rentalEndDate) {
      const days = this.calculatedRentalDays || 
        (window.VariantSelector ? VariantSelector.calculateDays(this.rentalStartDate, this.rentalEndDate) : 0);
      
      properties['Rental Start Date'] = this.rentalStartDate;
      properties['Rental End Date'] = this.rentalEndDate;
      properties['Rental Duration (Days)'] = days;
    }
    
    // Add to cart
    if (window.CartManager) {
      await CartManager.addItem(variantId, quantity, properties);
    } else {
      await this.addToCartFallback(variantId, quantity, properties);
    }
  }

  /**
   * Fallback add to cart
   */
  async addToCartFallback(variantId, quantity, properties) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity, properties }]
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    return await response.json();
  }

  /**
   * Show validation error
   */
  showValidationError() {
    if (this.requiredModuleError && this.modulesRequired) {
      this.requiredModuleError.classList.remove('d-none');
      this.requiredModuleError.classList.add('d-block');
      
      // Use ScrollUtils for header-aware scrolling
      if (window.ScrollUtils) {
        window.ScrollUtils.scrollToElement(this.requiredModuleError, {
          extraOffset: 20,
          behavior: 'smooth'
        });
      } else {
        this.requiredModuleError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      const minSelections = parseInt(this.requiredModuleCount?.dataset.minSelections || 0);
      const maxSelections = parseInt(this.requiredModuleCount?.dataset.maxSelections || minSelections);
      const maxLabel = maxSelections > 999 ? 'unlimited' : maxSelections;
      const selectedCount = this.getSelectedRequiredModules().length;

      // Get unique module types that are NOT selected
      const allModuleTypes = this.getAvailableModuleTypes();
      const selectedModuleTypes = this.getSelectedModuleTypes();
      const unselectedTypes = allModuleTypes.filter(type => !selectedModuleTypes.includes(type));
      
      // Build module type descriptor
      let moduleTypeText = 'module';
      if (unselectedTypes.length > 0) {
        moduleTypeText = unselectedTypes.join(' or ');
      } else if (allModuleTypes.length > 0) {
        moduleTypeText = allModuleTypes.join(' or ');
      }

      let errorMessage;
      if (minSelections === maxSelections) {
        errorMessage = `Please select exactly ${minSelections} ${moduleTypeText}(s) before adding to cart.`;
      } else if (selectedCount < minSelections) {
        errorMessage = `Please select at least ${minSelections} ${moduleTypeText}(s) before adding to cart. (Currently selected: ${selectedCount})`;
      } else if (selectedCount > maxSelections) {
        errorMessage = `Please select no more than ${maxLabel} ${moduleTypeText}(s) before adding to cart. (Currently selected: ${selectedCount})`;
      } else {
        errorMessage = `Please select between ${minSelections} and ${maxLabel} ${moduleTypeText}(s) before adding to cart.`;
      }

      alert(errorMessage);
    } else {
      alert('Please complete all required selections before adding to cart.');
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get current variant
   */
  getCurrentVariant() {
    return this.currentVariant;
  }

  /**
   * Get all variants
   */
  getVariants() {
    return this.variants;
  }

  /**
   * Get current transaction type
   */
  getCurrentTransactionType() {
    return this.currentTransactionType;
  }

  /**
   * Get current rental duration from variant
   */
  getCurrentRentalDuration() {
    return this.currentVariant?.option2 || this.currentVariant?.option1 || null;
  }

  /**
   * Format price utility
   */
  formatPrice(price) {
    return window.formatMoney(Math.round(price * 100));
  }
}


/**
 * Transaction Selector Sync
 */
function initTransactionSelectorSync() {
  console.log('🔄 Transaction Selector Sync initializing...');

  const selectors = document.querySelectorAll('[data-transaction-selector]');
  if (selectors.length === 0) return;

  const selectorGroups = {};
  
  selectors.forEach(selector => {
    const sectionId = selector.getAttribute('data-section-id');
    if (!selectorGroups[sectionId]) {
      selectorGroups[sectionId] = [];
    }
    selectorGroups[sectionId].push(selector);
  });

  Object.keys(selectorGroups).forEach(sectionId => {
    setupSelectorGroup(sectionId, selectorGroups[sectionId]);
  });

  console.log('✅ Transaction Selector Sync initialized');
}

function setupSelectorGroup(sectionId, selectors) {
  const config = window.transactionSelectors?.[sectionId];
  if (!config) {
    console.warn('⚠️ No config found for section:', sectionId);
    return;
  }

  console.log('🔄 Setting up selector group for section:', sectionId, 'config:', config);

  const rentRadio = document.getElementById(config.rentId);
  const purchaseRadio = document.getElementById(config.purchaseId);
  const previewOptions = [];

  selectors.forEach(selector => {
    const options = selector.querySelectorAll('[data-transaction-option]');
    console.log('🔍 Found preview options:', options.length, 'in selector:', selector);
    options.forEach(option => previewOptions.push(option));
  });

  console.log('✅ Total preview options found:', previewOptions.length);

  let isSyncing = false;

  function syncToType(type) {
    if (isSyncing) return;
    isSyncing = true;

    console.log('🔄 Syncing to type:', type, 'previewOptions:', previewOptions.length);

    // Update radio button and manually trigger change event
    let radioChanged = false;
    if (type === 'rent' && rentRadio && !rentRadio.disabled && !rentRadio.checked) {
      rentRadio.checked = true;
      radioChanged = true;
    } else if (type === 'purchase' && purchaseRadio && !purchaseRadio.disabled && !purchaseRadio.checked) {
      purchaseRadio.checked = true;
      radioChanged = true;
    }

    // Update preview options visual state
    previewOptions.forEach(option => {
      const optionType = option.getAttribute('data-transaction-option');
      if (optionType === type) {
        console.log('✅ Adding selected class to:', optionType, option);
        option.classList.add('selected');
      } else {
        console.log('➖ Removing selected class from:', optionType, option);
        option.classList.remove('selected');
      }
    });

    isSyncing = false;
    
    // Dispatch custom event for rental rates visibility
    document.dispatchEvent(new CustomEvent('transactionTypeChanged', {
      detail: { type, sectionId }
    }));

    // Manually trigger change event on radio if it was changed
    if (radioChanged) {
      const changedRadio = type === 'rent' ? rentRadio : purchaseRadio;
      if (changedRadio) {
        console.log('🔄 Triggering change event on radio:', type);
        changedRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  if (rentRadio) {
    rentRadio.addEventListener('change', function() {
      if (this.checked && !isSyncing) {
        syncToType('rent');
      }
    });
  }

  if (purchaseRadio) {
    purchaseRadio.addEventListener('change', function() {
      if (this.checked && !isSyncing) {
        syncToType('purchase');
      }
    });
  }

  previewOptions.forEach(option => {
    option.addEventListener('click', function() {
      if (this.getAttribute('data-disabled') === 'true') return;

      const type = this.getAttribute('data-transaction-option');
      syncToType(type);

      const configurator = document.getElementById('configurator');
      if (configurator) {
        setTimeout(() => {
          if (window.ScrollUtils) {
            window.ScrollUtils.scrollToElement(configurator, {
              extraOffset: 20,
              behavior: 'smooth'
            });
          } else {
            configurator.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    });
  });

  const initialType = config.hasRent ? 'rent' : (config.hasPurchase ? 'purchase' : null);
  if (initialType) {
    syncToType(initialType);
  }

  previewOptions.forEach(option => {
    const type = option.getAttribute('data-transaction-option');
    const isDisabled = (type === 'rent' && !config.hasRent) || (type === 'purchase' && !config.hasPurchase);
    
    if (isDisabled) {
      option.classList.add('disabled');
      option.setAttribute('data-disabled', 'true');
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productConfigurator = new ProductConfigurator();
    window.VariantManager = window.productConfigurator; // Backwards compatibility
    
    initTransactionSelectorSync();
    
    // Initialize tooltips asynchronously - won't block page load
    // Uses requestIdleCallback if available for better performance
    if (window.requestIdleCallback) {
      requestIdleCallback(() => {
        window.productConfigurator?.initializeTransactionTooltips();
      }, { timeout: 2000 });
    } else {
      setTimeout(() => {
        window.productConfigurator?.initializeTransactionTooltips();
      }, 200);
    }
  });
} else {
  window.productConfigurator = new ProductConfigurator();
  window.VariantManager = window.productConfigurator;
  
  initTransactionSelectorSync();
  
  // Initialize tooltips asynchronously - won't block page load
  if (window.requestIdleCallback) {
    requestIdleCallback(() => {
      window.productConfigurator?.initializeTransactionTooltips();
    }, { timeout: 2000 });
  } else {
    setTimeout(() => {
      window.productConfigurator?.initializeTransactionTooltips();
    }, 200);
  }
}
